import { VFC, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Grid,
  Box,
  Button,
  TextField,
  MenuItem,
  Select,
  FormHelperText,
  FormControl,
  Autocomplete,
  Typography,
  SelectChangeEvent,
  IconButton,
} from "@mui/material";
import ButtonArea from "../common/ButtonArea";
import { AlertDialog } from "../common/dialog/AlertDialog";
import { FormLabel } from "../common/form/FormLabel";
import { FormPullDown, PullDownItem } from "../common/form/FormPullDown";

import {
  ContractEditFormSchema,
  ContractEditForm,
  ContractCreateFormSchema,
} from "./api/ContractForm";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ContractService } from "./api/ContractService";
import { AxiosResponse } from "axios";
import { ResultResponse, ResultDialog } from "../common/dialog/ResultDialog";
import { Utility } from "../../utils/Utility";
import Constants from "../../utils/Constants";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ja } from "date-fns/locale";
import { TenantService } from "../tenant/api/TenantService";
import { TenantNameRequest, TenantNameResponse, TenantSearchRequest, TenantSearchResponse } from "../tenant/api/TenantForm";
import { ContractEditMode } from "../../entity/ContractEditMode";
import { CancelButton } from "../common/CancelButton";
import { Loading } from "../common/Loading";
import { ContractUserService } from "../contractuser/api/ContractUserService";
import { ContractUserNameRequest, ContractUserNameResponse } from "../contractuser/api/ContractUserForm";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ContractUserDialogMode from "../../entity/ContractUserDialogMode";
import ContractUserDialog from "../contractuser/ContractUserEditDialog";
import { ProtectedApiAuth } from "../protected/ProtectedApiAuth";

/**
 * 契約編集画面
 */
const ContractEdit: VFC = () => {
  const navigate = useNavigate();
  const WIDTH = 10;
  const locate = useLocation();
  const contractStatusOptions = Utility.getContractStatusOption();
  const contractTrialOptions = Utility.getContractTrialOption();
  /** locate.state.mode */
  const MODE = locate.state.mode as ContractEditMode;
  const PAGE_NAME = (MODE === ContractEditMode.CREATE) ? "契約追加" : 
                    (MODE === ContractEditMode.DUPLICATE) ? "契約複製" : 
                                                            "契約編集";
  /***************************************************************
   * State管理
   **************************************************************/
  // 共通ローディング
  const [isLoading, setIsLoading] = useState(false);
  
  // 契約ID
  const [contractId] = useState(sessionStorage.getItem("CONTRACT_ID") ?? "");

  // 契約情報
  const [contractEditForm, setContractEditForm] = useState(new ContractEditForm());

  // テナント選択肢
  const [tenantOptions, setTenantOptions] = useState<PullDownItem[]>([] as PullDownItem[]);

  // 組織選択肢
  // const [organizationOptions, setOrganizationOptions] = useState<PullDownItem[]>([] as PullDownItem[]);

  // 契約管理システムユーザー選択肢
  const [contractUserOptions, setContractUserOptions] = useState<PullDownItem[]>([] as PullDownItem[]);

  // MDMユーザーダイアログ用のID
  const [contractUserDialogId, setContractUserDialogId] = useState("");

  // MDMユーザーダイアログ
  const [isContractUserOpen, setIsContractUserOpen] = useState(false);

  // 新規作成・編集・複製確認ダイアログ
  const [isEditAlertDialogOpen, setIsEditAlertDialogOpen] = useState(false);

  // 削除確認ダイアログ
  const [isDeleteAlertDialogOpen, setIsDeleteAlertDialogOpen] = useState(false);

  // 共通ダイアログ
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // 処理結果
  const [resultResponse, setResultResponse] = useState<ResultResponse>({
    status: "",
    code: "",
    displayMessage: "",
    result: [],
    errorMessages: [],
  });

  /***************************************************************
   * 共通処理
   **************************************************************/
  // 処理結果ダイアログ(CLOSE)
  const handleResultDialogClose = () => {
    setIsDialogOpen(false);
    if (resultResponse.status === "OK") {
      navigate(-1);
    }
  };

  // バックエンドからのメッセージを設定
  const setBackendValidMessage = (response: any) => {
    let error = response;
    if (error.tenantId) {
      setError("tenantId", { message: error.tenantId });
    }
    if (error.contractStart) {
      setError("contractStart", { message: error.contractStart });
    }
    if (error.contractEnd) {
      setError("contractEnd", { message: error.contractEnd });
    }
    if (error.contractDevice) {
      setError("contractDevice", { message: error.contractDevice });
    }
    if (error.contractPeople) {
      setError("contractPeople", { message: error.contractPeople });
    }
    if (error.status) {
      setError("status", { message: error.status });
    }
    if (error.trial) {
      setError("trial", { message: error.trial });
    }
    if (error.entryPrice) {
      setError("entryPrice", { message: error.entryPrice });
    }
    if (error.contractPrice) {
      setError("contractPrice", { message: error.contractPrice });
    }
    // if (error.organizationId) {
    //   setError("organizationId", { message: error.organizationId });
    // }
    if (error.adminUserName1) {
      setError("adminUserName1", { message: error.adminUserName1 });
    }
    if (error.adminUserName2) {
      setError("adminUserName2", { message: error.adminUserName2 });
    }
    if (error.salesStaffId) {
      setError("salesStaffId", { message: error.salesStaffId });
    }
    if (error.comment1) {
      setError("comment1", { message: error.comment1 });
    }
    if (error.comment2) {
      setError("comment2", { message: error.comment2 });
    }
  };
  
  // 処理結果を設定
  const setStateResultResponse = (
    status: string,
    code: string,
    displayMessage: string,
    result: string
  ) => {
    setIsDialogOpen(true);
    setResultResponse({
      status,
      code,
      displayMessage,
      result: [result],
      errorMessages: [],
    });
  };

  /***************************************************************
   * 初期表示処理
   **************************************************************/
  useEffect(() => {
    // getOrganizationNameList();
    getTenantNameList();
    getContractUserNameList();
    if ("" !== contractId) {
      // 契約情報取得
      findByEditId();
    }
  }, []);

  // テナント名一覧取得API
  const getTenantNameList = () => {
    const service = new TenantService();
    service
      .findTenantNameList(new TenantNameRequest)
      .then((response: AxiosResponse) => {
        const tenantNameList: TenantNameResponse[] = response.data.data;
        setTenantOptions(Utility.getTenantNameOption(tenantNameList));
      })
      .catch((err) => {})
      .finally(() => {});
  }

  // 組織名一覧取得API
  // const getOrganizationNameList = () => {
  //   const service = new OrganizationService();
  //   service
  //     .findOrganizationNameList(new OrganizationNameRequest)
  //     .then((response: AxiosResponse) => {
  //       const organizationNameList: OrganizationNameResponse[] = response.data.data;
  //       setOrganizationOptions(Utility.getOrganizaitonNameOption(organizationNameList));
  //     })
  //     .catch((err) => {})
  //     .finally(() => {}); 
  // }

  // 契約管理システムユーザー一覧取得API
  const getContractUserNameList = () => {
    const service = new ContractUserService();
    service
      .findContractUserNameList(new ContractUserNameRequest)
      .then((response: AxiosResponse) => {
        const contractUserNameList: ContractUserNameResponse[] = response.data.data;
        setContractUserOptions(Utility.getContractUserNameOption(contractUserNameList));
      })
      .catch((err) => {})
      .finally(() => {});
  }

  // 契約情報取得API
  const findByEditId = () => {
    if ("" !== contractId) {
      setIsLoading(true);
      const service = new ContractService();
      service
        .findByEditId(contractId)
        .then((response: AxiosResponse) => {
          const contract = response.data.result;
          setValue("id", contractId);
          setValue("disclosureCode", contract.disclosureCode);
          setValue("tenantId", contract.tenantId);
          setValue("companyName", contract.companyName);
          setValue("startView", new Date(contract.contractStart));
          setValue("endView", new Date(contract.contractEnd));
          setValue("contractDevice", contract.contractDevice);
          setValue("contractPeople", contract.contractPeople);
          setValue("status", contract.status);
          setValue("trial", contract.trial);
          setValue("entryPrice", contract.entryPrice);
          setValue("contractPrice", contract.contractPrice);
          // setValue("organizationId", contract.organizationId);
          // setValue("organizationName", contract.organizationName);
          setValue("adminUserName1", contract.adminUserName1);
          setValue("adminUserName2", contract.adminUserName2);
          setValue("salesStaffId", contract.salesStaffId);
          setValue("salesStaffName", contract.salesStaffName);
          setValue("comment1", contract.comment1);
          setValue("comment2", contract.comment2);
          setValue("createUser", contract.createUser);
          setValue("createDate", contract.createDate);
          setValue("updateUser", contract.updateUser);
          setValue("updateDate", contract.updateDate);
          setValue("deleteDate", contract.deleteDate);

          setContractEditForm(response.data.result);
        })
        .catch((err) => {
          setStateResultResponse(
            Constants.ERR_STATUS_NG,
            err.response.status,
            err.response.statusText,
            Constants.E_MSG_SYSTEM_ERR
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
      } else {
        setStateResultResponse(
          Constants.ERR_STATUS_NG,
          Constants.ERR_CODE_404,
          Constants.T_MSG_NO_DATA,
          Constants.W_MSG_NO_DATA
        );
      }
  };

  /***************************************************************
   * 新規作成・編集処理
   **************************************************************/
  const handleEditAlertOpen = () => {
    setIsEditAlertDialogOpen(true);
  };
  // 新規作成・編集確認ダイアログ(CLOSE)
  const handleEditAlertClose = () => {
    setIsEditAlertDialogOpen(false);
  };
  // 保存ボタン押下(入力チェック)
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    setError,
    watch,
    control,
    formState: { errors },
  } = useForm<ContractEditForm>({
    resolver: yupResolver(
      MODE === ContractEditMode.EDIT ?  ContractEditFormSchema : ContractCreateFormSchema
    ),
    reValidateMode: "onSubmit",
    defaultValues: new ContractEditForm(),
  });

  // 契約情報新規作成確認API or 契約情報編集確認API
  const handleEditBtn: SubmitHandler<ContractEditForm> = (hookForm) => {
    const service = new ContractService();
    switch (MODE) {
      case ContractEditMode.CREATE:
      case ContractEditMode.DUPLICATE:
        // 新規作成 or 複製
        service
          .createConfirm(hookForm)
          .then((response) => {
            if (Constants.ERR_STATUS_OK === response.data.status) {
              // OK
              setContractEditForm(getValues());
              handleEditAlertOpen();
            } else if (Constants.ERR_STATUS_INVALID === response.data.status) {
              // 入力違反
              setBackendValidMessage(response.data.errorMessages);
            } else {
              // NG
              setIsDialogOpen(true);
              setResultResponse(response.data);
            }
          })
          .catch((err) => {
            setStateResultResponse(
              Constants.ERR_STATUS_NG,
              err.response.status,
              err.response.statusText,
              Constants.E_MSG_SYSTEM_ERR
            );
          })
          .finally(() => {
            // TODO 必要ならローディング
          });
        break;
      case ContractEditMode.EDIT:
      // 契約情報編集確認API
        service
          .updateConfirm(hookForm)
          .then((response) => {
            if (Constants.ERR_STATUS_OK === response.data.status) {
              // OK
              setContractEditForm(getValues());
              handleEditAlertOpen();
            } else if (Constants.ERR_STATUS_INVALID === response.data.status) {
              // 入力違反
              setBackendValidMessage(response.data.errorMessages);
            } else {
              // NG
              setIsDialogOpen(true);
              setResultResponse(response.data);
            }
          })
          .catch((err) => {
            setStateResultResponse(
              Constants.ERR_STATUS_NG,
              err.response.status,
              err.response.statusText,
              Constants.E_MSG_SYSTEM_ERR
            );
          })
          .finally(() => {
            // TODO 必要ならローディング
          });
        break;
    }
  };
  // 契約情報新規作成API or 契約情報編集API
  const handleEditConfirmBtn = () => {
    const service = new ContractService();

    handleEditAlertClose();
    setContractEditForm(getValues());

    switch (MODE) {
      case ContractEditMode.CREATE:
      case ContractEditMode.DUPLICATE:
        service
          .create(contractEditForm)
          .then((response) => {
            if (Constants.ERR_STATUS_OK === response.data.status) {
              // OK
              setIsDialogOpen(true);
              setResultResponse(response.data);
            } else if (Constants.ERR_STATUS_INVALID === response.data.status) {
              // 入力違反
              setBackendValidMessage(response.data.errorMessages);
            } else {
              // NG
              setIsDialogOpen(true);
              setResultResponse(response.data);
            }
          })
          .catch((err) => {
            setStateResultResponse(
              Constants.ERR_STATUS_NG,
              err.response.status,
              err.response.statusText,
              Constants.E_MSG_SYSTEM_ERR
            );
          })
          .finally(() => {
            // TODO 必要ならローディング
          });
        break;
      case ContractEditMode.EDIT:
        // 契約情報編集API
        service
          .update(contractEditForm)
          .then((response) => {
            if (Constants.ERR_STATUS_OK === response.data.status) {
              // OK
              setIsDialogOpen(true);
              setResultResponse(response.data);
            } else if (Constants.ERR_STATUS_INVALID === response.data.status) {
              // 入力違反
              setBackendValidMessage(response.data.errorMessages);
            } else {
              // NG
              setIsDialogOpen(true);
              setResultResponse(response.data);
            }
          })
          .catch((err) => {
            setStateResultResponse(
              Constants.ERR_STATUS_NG,
              err.response.status,
              err.response.statusText,
              Constants.E_MSG_SYSTEM_ERR
            );
          })
          .finally(() => {
            // TODO 必要ならローディング
          });
    }
  };

  /***************************************************************
   * 削除処理
   **************************************************************/
  // 削除確認ダイアログ(OPEN)
  const handleDeleteAlertOpen = () => {
    setIsDeleteAlertDialogOpen(true);
  };
  // 削除確認ダイアログ(CLOSE)
  const handleDeleteAlertClose = () => {
    setIsDeleteAlertDialogOpen(false);
  };
  // 削除ボタン押下
  const handleDeleteBtn = () => {
    const service = new ContractService();

    if ("" !== contractId) {
      setIsDeleteAlertDialogOpen(false);
      service
        .delete(contractId)
        .then((response) => {
          if ("OK" === response.data.status) {
            setIsDialogOpen(true);
            setResultResponse(response.data);
          } else {
            setIsDialogOpen(true);
            setResultResponse(response.data);
          }
        })
        .catch((err) => {
          setStateResultResponse(
            Constants.ERR_STATUS_NG,
            err.response.status,
            err.response.statusText,
            Constants.E_MSG_SYSTEM_ERR
          );
        })
        .finally(() => {
          // TODO 必要ならローディング
        });
    }
  };

  /***************************************************************
   * 契約管理ユーザー詳細表示処理
   **************************************************************/
  // ユーザーダイヤログ（OPEN）
  const showUserButtonClick = (id: string) => {
    // 画面遷移なしでポップアップ表示
    setContractUserDialogId(id);
    setIsContractUserOpen(true);
  }

  // ユーザーダイヤログ（CLOSE）
  const handleMdmUserClose = () => {
    setIsContractUserOpen(false);
  }

  /***************************************************************
   * 権限処理
   **************************************************************/
  // 権限なしのオーバーライド
  const overrideFunction = (response: ResultResponse) => {
    setResultResponse(response);
    setIsDialogOpen(true);
  }

  /***************************************************************
   * レイアウト
   **************************************************************/

  return (
    <>
      <Box>
        {/* ボタンエリア */}
        <ButtonArea pageName={PAGE_NAME}>
          {/* 削除ボタン */}
          <ProtectedApiAuth
            authcd={Constants.AUTH_CONTRACT_DELETE}
            defaultFunction={handleDeleteAlertOpen}
            overrideFunction={overrideFunction}
          >
            <Button
              variant="contained"
              color="error"
              disabled={MODE !== ContractEditMode.EDIT}
            >
              契約削除
            </Button>
          </ProtectedApiAuth>
          {/* ボタン1つ分の空白 */}
          <Button
            disabled={true}
          >
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit(handleEditBtn)}
          >
            保存
          </Button>
          <CancelButton/>
        </ButtonArea>

        {/* 入力フォーム */}
        <Box sx={{ p: 4 }}>
          <Box sx={{ py: 2, px: 2 }}>
            <Grid container spacing={2} alignItems="center">
              {/* 契約ID */}
              {MODE === ContractEditMode.EDIT &&
                <>
                  <FormLabel label="契約ID" />
                  <Grid item xs={WIDTH}>
                    <Typography noWrap component="div" align="left">
                      {contractId}
                    </Typography>
                  </Grid>
                </>
              }
              {/* 公開コード */}
              {MODE === ContractEditMode.EDIT &&
                <>
                  <FormLabel label="契約コード" />
                  <Grid item xs={WIDTH}>
                    <Typography noWrap component="div" align="left">
                      {getValues("disclosureCode")}
                    </Typography>
                  </Grid>
                </>
              }
              {/* テナント名 */}
              <FormLabel label="テナント名" isMandatory={true} />
              <Grid item xs={WIDTH}>
                <FormControl fullWidth>
                  <Controller
                    control={control}
                    name="tenantId"
                    defaultValue=""
                    render={({ field }) => (
                      <>
                        <Select
                          size="small"
                          {...field}
                          {...register("tenantId")}
                          error={"tenantId" in errors}
                        >
                          <MenuItem value="">選択してください</MenuItem>
                          {tenantOptions?.map((item) => (
                            <MenuItem key={item.key} value={item.key}>
                              {item.value}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.tenantId && (
                          <FormHelperText error>
                            {errors.tenantId?.message}
                          </FormHelperText>
                        )}
                      </>
                    )}
                  />
                </FormControl>
              </Grid>
              {/* 組織名 */}
              {/* <FormLabel label="組織名" />
              <Grid item xs={WIDTH}>
                <FormControl fullWidth>
                  <Controller
                    control={control}
                    name="organizationId"
                    defaultValue=""
                    render={({ field }) => (
                      <>
                        <Select
                          size="small"
                          {...field}
                          {...register("organizationId")}
                        >
                          <MenuItem value="">選択してください</MenuItem>
                          {organizationOptions?.map((item) => (
                            <MenuItem key={item.key} value={item.key}>
                              {item.value}
                            </MenuItem>
                          ))}
                        </Select>
                      </>
                    )}
                  />
                </FormControl>
              </Grid> */}
              {/* 契約期間 */}
              <FormLabel label="契約期間" isMandatory={true} />
              <Grid item xs={WIDTH} display="flex" alignItems="center">
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  adapterLocale={ja}
                  dateFormats={{ monthAndYear: "yyyy年 MM月" }}
                  localeText={{
                    previousMonth: "前月を表示",
                    nextMonth: "次月を表示",
                  }}
                >
                  <Controller
                    name="startView"
                    control={control}
                    render={({ field }) => {
                      return (
                        <>
                          <DatePicker
                            label="契約期間 開始日"
                            slotProps={{
                              textField: {
                                size: 'small',
                                InputLabelProps: { shrink: true },
                                fullWidth: true,
                              },
                            }}
                            {...field}
                          />
                          {errors.startView && (
                            <FormHelperText error>
                              {errors.startView.message}
                            </FormHelperText>
                          )}
                        </>
                      );
                    }}
                  />
                </LocalizationProvider>
                <Typography component="span">～</Typography>
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  adapterLocale={ja}
                  dateFormats={{ monthAndYear: "yyyy年 MM月" }}
                  localeText={{
                    previousMonth: "前月を表示",
                    nextMonth: "次月を表示",
                  }}
                >
                  <Controller
                    name="endView"
                    control={control}
                    render={({ field }) => {
                      return (
                        <>
                          <DatePicker
                            label="契約期間 終了日"
                            slotProps={{
                              textField: {
                                size: 'small',
                                InputLabelProps: { shrink: true },
                                fullWidth: true,
                              },
                            }}
                            {...field}
                          />
                          {errors.endView && (
                            <FormHelperText error>
                              {errors.endView.message}
                            </FormHelperText>
                          )}
                        </>
                      );
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              {/* 契約台数 */}
              <FormLabel label="契約台数" isMandatory={true} />
              <Grid item xs={WIDTH}>
                <FormControl fullWidth>
                  <TextField
                    fullWidth
                    size="small"
                    {...register("contractDevice")}
                    error={"contractDevice" in errors}
                    helperText={errors.contractDevice?.message}
                  />
                </FormControl>
              </Grid>
              {/* 契約人数 */}
              <FormLabel label="契約人数" isMandatory={true} />
              <Grid item xs={WIDTH}>
                <FormControl fullWidth>
                  <TextField
                    fullWidth
                    size="small"
                    {...register("contractPeople")}
                    error={"contractPeople" in errors}
                    helperText={errors.contractPeople?.message}
                  />
                </FormControl>
              </Grid>
              {/* 契約ステータス */}
              <FormLabel label="契約ステータス" />
              <Grid item xs={WIDTH}>
                <FormControl fullWidth>
                  <Controller
                    control={control}
                    name="status"
                    defaultValue=""
                    render={({ field }) => (
                      <>
                        <Select
                          size="small"
                          {...field}
                          {...register("status")}
                        >
                          <MenuItem value="">選択してください</MenuItem>
                          {contractStatusOptions?.map((item) => (
                            <MenuItem key={item.key} value={item.key}>
                              {item.value}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.status && (
                          <FormHelperText error>
                            {errors.status?.message}
                          </FormHelperText>
                        )}
                      </>
                    )}
                  />
                </FormControl>
              </Grid>
              {/* トライアル */}
              <FormLabel label="トライアル" />
              <Grid item xs={WIDTH}>
                <FormControl fullWidth>
                  <Controller
                    control={control}
                    name="trial"
                    defaultValue=""
                    render={({ field }) => (
                      <>
                        <Select
                          size="small"
                          {...field}
                          {...register("trial")}
                        >
                          <MenuItem value="">選択してください</MenuItem>
                          {contractTrialOptions?.map((item) => (
                            <MenuItem key={item.key} value={item.key}>
                              {item.value}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.trial && (
                          <FormHelperText error>
                            {errors.trial?.message}
                          </FormHelperText>
                        )}
                      </>
                    )}
                  />
                </FormControl>
              </Grid>
              {/* 端末登録料 */}
              <FormLabel label="端末登録料" />
              <Grid item xs={WIDTH}>
                <FormControl fullWidth>
                  <TextField
                    fullWidth
                    size="small"
                    {...register("entryPrice")}
                  />
                </FormControl>
              </Grid>
              {/* 月額利用料 */}
              <FormLabel label="月額利用料" isMandatory={true} />
              <Grid item xs={WIDTH}>
                <FormControl fullWidth>
                  <TextField
                    fullWidth
                    size="small"
                    {...register("contractPrice")}
                    error={"contractPrice" in errors}
                    helperText={errors.contractPrice?.message}
                  />
                </FormControl>
              </Grid>
              {/* お客様管理者氏名1 */}
              <FormLabel label="お客様管理者氏名1" />
              <Grid item xs={WIDTH}>
                <FormControl fullWidth>
                  <TextField
                    fullWidth
                    size="small"
                    {...register("adminUserName1")}
                  />
                </FormControl>
              </Grid>
              {/* お客様管理者氏名2 */}
              <FormLabel label="お客様管理者氏名2" />
              <Grid item xs={WIDTH}>
                <FormControl fullWidth>
                  <TextField
                    fullWidth
                    size="small"
                    {...register("adminUserName2")}
                  />
                </FormControl>
              </Grid>
              {/* 営業担当者氏名 */}
              <FormLabel label="営業担当者氏名" />
              <Grid item xs={watch("salesStaffId") !== "" && contractUserOptions.map(user => user.key).includes(watch("salesStaffId")) ? WIDTH - 1 : WIDTH}>
                <FormControl fullWidth>
                  <Controller
                    control={control}
                    name="salesStaffId"
                    render={({ field: { ref, onChange, ...field } }) => (
                      <>
                        <Autocomplete
                          {...field}
                          size="small"
                          fullWidth
                          noOptionsText={"候補が見つかりません"}
                          options={contractUserOptions ?? []}
                          getOptionLabel={(option) => option.value}
                          isOptionEqualToValue={(option, v) =>
                            option.key === v.key
                          }
                          value={contractUserOptions.filter(item => item.key === watch("salesStaffId"))[0] ?? null}
                          onChange={(e, value) => {
                            setValue("salesStaffId", value !== null ? value.key : "");
                          }}
                          renderInput={(params) => {
                            return (
                              <TextField
                                {...field}
                                {...params}
                                fullWidth
                                inputRef={ref}
                              />
                            );
                          }}
                        />
                      </>
                    )}
                  />
                </FormControl>
              </Grid>
              {watch("salesStaffId") !== "" && contractUserOptions.map(user => user.key).includes(watch("salesStaffId")) ? <>
                <Grid item xs={1}>
                  <ProtectedApiAuth
                    authcd={Constants.AUTH_CONTRACT_DETAIL}
                    defaultFunction={() => showUserButtonClick(getValues("salesStaffId"))}
                    overrideFunction={overrideFunction}
                  >
                    <IconButton>
                      <InfoOutlinedIcon/>
                    </IconButton>
                  </ProtectedApiAuth>
                </Grid>
              </> : <></>}
              {/* 備考1 */}
              <FormLabel label="備考1" />
              <Grid item xs={WIDTH}>
                <FormControl fullWidth>
                  <TextField
                    fullWidth
                    size="small"
                    {...register("comment1")}
                  />
                </FormControl>
              </Grid>
              {/* 備考2 */}
              <FormLabel label="備考2" />
              <Grid item xs={WIDTH}>
                <FormControl fullWidth>
                  <TextField
                    fullWidth
                    size="small"
                    {...register("comment2")}
                  />
                </FormControl>
              </Grid>
              {MODE === ContractEditMode.EDIT &&
                <>
                  {/* 作成者 */}
                  <FormLabel label="作成者" />
                  <Grid item xs={WIDTH}>
                    <Typography noWrap component="div" align="left">
                      {contractEditForm.createUser}
                    </Typography>
                  </Grid>
                  {/* 作成日時 */}
                  <FormLabel label="作成日時" />
                  <Grid item xs={WIDTH}>
                    <Typography noWrap component="div" align="left">
                      {contractEditForm.createDate}
                    </Typography>
                  </Grid>
                  {/* 更新者 */}
                  <FormLabel label="更新者" />
                  <Grid item xs={WIDTH}>
                    <Typography noWrap component="div" align="left">
                      {contractEditForm.updateUser}
                    </Typography>
                  </Grid>
                  {/* 更新日時 */}
                  <FormLabel label="更新日時" />
                  <Grid item xs={WIDTH}>
                    <Typography noWrap component="div" align="left">
                      {contractEditForm.updateDate}
                    </Typography>
                  </Grid>
                  {/* 削除日時 */}
                  <FormLabel label="削除日時" />
                  <Grid item xs={WIDTH}>
                    <Typography noWrap component="div" align="left">
                      {contractEditForm.deleteDate}
                    </Typography>
                  </Grid>
                </>
              }

              {/* 処理ローディング */}
              <Loading isLoading={isLoading} />
            </Grid>
          </Box>
        </Box>
      </Box>

      {/* MDMユーザーダイアログ */}
      <ContractUserDialog
        isAlertDialogOpen={isContractUserOpen}
        mode="保存"
        contractUserId={contractUserDialogId}
        defaultDialogMode={ContractUserDialogMode.DETAIL}
        handleAlertCancel={handleMdmUserClose}
        handleAlertOk={getContractUserNameList} 
      />

      {/* 新規作成・編集確認ダイアログ */}
      <AlertDialog
        isAlertDialogOpen={isEditAlertDialogOpen}
        title="保存確認"
        message="保存しますか？"
        mode="保存"
        handleAlertCancel={handleEditAlertClose}
        handleAlertOk={handleEditConfirmBtn}
      />

      {/* 削除確認ダイアログ */}
      <AlertDialog
        isAlertDialogOpen={isDeleteAlertDialogOpen}
        title="削除確認"
        message="削除しますか？"
        mode="削除"
        handleAlertCancel={handleDeleteAlertClose}
        handleAlertOk={handleDeleteBtn}
      />

      {/* 処理結果ダイアログ */}
      <ResultDialog
        isDialogOpen={isDialogOpen}
        resultResponse={resultResponse}
        handler={handleResultDialogClose}
      />
    </>
  );
};

export default ContractEdit;
